import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { InfoButton, levelNoun, newLabel, nounOf, TagLabel, TagPicker, type TagPickerValue } from "@vd/ui";
import { Lock, Plus } from "lucide-react";
import { useState } from "react";
import { type CampaignDetail, campaignQuery, campaignsApi, ck } from "../../../api/campaigns";
import { errorMessage } from "../../../api/client";
import { campaignDispositionsQuery } from "../../../api/dispositions";
import { companyPicksQuery, mailboxesApi, mailboxKeys, pickFor } from "../../../api/mailboxes";
import { campaignVmDefaultQuery, clockMs, vmDrops, vmKeys } from "../../../api/vmdrops";
import { isAdmin, requireManager, useMe } from "../../../auth/useMe";
import { Crumbs } from "../../../components/Crumbs";
import { BriefCard } from "../../../components/campaigns/BriefCard";
import { CallFieldsCard } from "../../../components/campaigns/CallFieldsCard";
import { CampaignAnalyticsCard } from "../../../components/campaigns/CampaignAnalyticsCard";
import { CampaignDrawer } from "../../../components/campaigns/CampaignDrawer";
import { OwnerAvatar, Sharing } from "../../../components/campaigns/OwnerAvatar";
import { PeopleCard } from "../../../components/campaigns/PeopleCard";
import { pickerValue, searchTags, tagHelp } from "../../../components/campaigns/tags";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Loading,
  PageHeader,
  Select,
  Stat,
  Switch,
} from "../../../components/ui";
import { useCampaignName, useCampaignParents } from "../../../lib/crumbLabels";
import { Link, useNavigate } from "../../../lib/navigate";

export const Route = createFileRoute("/admin/campaigns/$campaignId")({
  staticData: {
    crumb: {
      label: "Campaign",
      useLabel: ({ params }) => useCampaignName(params.campaignId ?? ""),
      useParents: ({ params }) => useCampaignParents(params.campaignId ?? ""),
    },
  },
  beforeLoad: requireManager,
  component: CampaignPage,
});

function CampaignPage() {
  const { campaignId } = Route.useParams();
  const q = useQuery(campaignQuery(campaignId));
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  return <Campaign key={campaignId} d={q.data} />;
}

function useRefresh(d: CampaignDetail) {
  const qc = useQueryClient();
  return (next?: CampaignDetail) => {
    if (next) qc.setQueryData(ck.detail(d.campaign.id), next);
    else qc.invalidateQueries({ queryKey: ck.detail(d.campaign.id) });
    qc.invalidateQueries({ queryKey: ck.tree });
  };
}

function Campaign({ d }: { d: CampaignDetail }) {
  const c = d.campaign;
  const me = useMe();
  const admin = isAdmin(me);
  const navigate = useNavigate();
  const refresh = useRefresh(d);
  const [adding, setAdding] = useState(false);
  const root = !c.parent_id;
  const s = d.stats_30d;

  const archive = async () => {
    try {
      await campaignsApi.update(c.id, { status: c.status === "archived" ? "active" : "archived" });
      refresh();
      toast(`${nounOf(c, { title: true })} ${c.status === "archived" ? "restored" : "archived"}.`);
    } catch (e) {
      toast(errorMessage(e), "danger");
    }
  };

  return (
    <>
      <PageHeader
        title={c.name}
        crumbs={<Crumbs />}
        actions={
          <>
            {d.can_add_sub ? (
              <Button onClick={() => setAdding(true)}>
                <Plus className="size-4" /> {newLabel(c)}
              </Button>
            ) : null}
            {d.can_edit ? (
              <Button variant="ghost" onClick={archive}>
                {c.status === "archived" ? "Restore" : "Archive"}
              </Button>
            ) : null}
          </>
        }
      >
        {c.status === "archived" ? <Badge tone="warn">Archived</Badge> : null}
        {!c.ai.active ? <Badge tone="warn">Suggestions paused</Badge> : null}
      </PageHeader>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Calls, 30 days" value={s.calls} />
        <Stat label="Connected" value={s.connected} />
        <Stat label="Opportunities" value={s.opportunities_created} />
        <Stat label="Follow-ups booked" value={s.follow_ups_booked} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex flex-col gap-4">
          <BriefCard d={d} />
          <CampaignAnalyticsCard id={c.id} name={c.name} />
          {c.children.length ? (
            <Card title={levelNoun("campaign", { title: true, many: true })}>
              <ul className="flex flex-col">
                {c.children.map((k) => (
                  <li key={k.id}>
                    <button
                      type="button"
                      onClick={() =>
                        navigate({ to: "/admin/campaigns/$campaignId", params: { campaignId: k.id } })
                      }
                      className="flex min-h-14 w-full items-center justify-between gap-3 border-hairline border-b px-4 py-2 text-left last:border-b-0 hover:bg-fill-1"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="flex items-center gap-2 text-body">
                          {k.name}
                          <Sharing node={k} />
                        </span>
                        {k.tag.state === "none" ? (
                          <span className="truncate text-caption text-ink-3">No Odoo tag</span>
                        ) : (
                          <TagLabel path={k.tag.name ?? k.tag_default} className="mt-0.5" />
                        )}
                      </span>
                      <OwnerAvatar node={k} />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          <CallFieldsCard name={c.name} path={d.path.map((p) => p.id)} />
        </div>
        <div className="flex flex-col gap-4">
          <PeopleCard d={d} />
          <TagCard d={d} />
          <DispositionsCard id={c.id} />
          <VoicemailDefaultCard id={c.id} />
          <SendFromCard d={d} />
          {admin || c.owner_user_id === me.user.id ? (
            <Card title="Assistant">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-ui">Suggestions for every rep</span>
                <Switch
                  checked={c.ai.active}
                  label="Suggestions for every rep"
                  onChange={async (on) => {
                    try {
                      if (on && c.ai.paused_by) await campaignsApi.resume(c.ai.paused_by.pause_id);
                      else if (!on) await campaignsApi.pause(c.id);
                      refresh();
                    } catch (e) {
                      toast(errorMessage(e), "danger");
                    }
                  }}
                />
              </div>
            </Card>
          ) : null}
          {root && admin ? (
            <Card title="Reps">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-ui">May add their own campaigns</span>
                <Switch
                  checked={c.allow_user_subs}
                  label="Reps may add their own campaigns"
                  onChange={async (v) => {
                    try {
                      refresh(await campaignsApi.update(c.id, { allow_user_subs: v }));
                    } catch (e) {
                      toast(errorMessage(e), "danger");
                    }
                  }}
                />
              </div>
            </Card>
          ) : null}
        </div>
      </div>
      {adding ? (
        <CampaignDrawer
          parentId={c.id}
          onClose={() => setAdding(false)}
          onCreated={(id) => navigate({ to: "/admin/campaigns/$campaignId", params: { campaignId: id } })}
        />
      ) : null}
    </>
  );
}

function TagCard({ d }: { d: CampaignDetail }) {
  const c = d.campaign;
  const refresh = useRefresh(d);
  const [busy, setBusy] = useState(false);
  const exists = Boolean(c.tag.crm_id) && c.tag.name === c.tag_default;
  const change = async (v: TagPickerValue) => {
    setBusy(true);
    try {
      refresh(await campaignsApi.retryTag(c.id, v));
    } catch (e) {
      toast(errorMessage(e), "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      title={
        <span className="flex items-center gap-1">
          Odoo tag
          <InfoButton kind="help" label="About the Odoo tag">
            {tagHelp(nounOf(d.campaign))}
          </InfoButton>
        </span>
      }
    >
      <div className="flex flex-col gap-2 px-4 py-3">
        <TagPicker
          value={pickerValue(c)}
          onChange={(v) => void change(v)}
          search={searchTags}
          ownPath={c.tag_default}
          ownExists={exists}
          disabled={!d.can_tag || busy}
        />
        {c.tag.state === "pending" ? (
          <p className="text-caption text-ink-3">Made in Odoo when its owner shares it.</p>
        ) : c.tag.state === "failed" ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-caption text-danger-ink">{c.tag.error ?? "The tag wasn't made."}</p>
            <Button size="sm" busy={busy} disabled={!d.can_tag} onClick={() => void change(pickerValue(c))}>
              Retry
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** The campaign's dispositions in one line, inherited or its own, with Edit (ADR 0015). */
function DispositionsCard({ id }: { id: string }) {
  const q = useQuery(campaignDispositionsQuery(id));
  const list = q.data?.dispositions ?? [];
  const names = list.map((d) => d.label);
  const line =
    names.length > 3
      ? `${names.slice(0, 2).join(", ")}, ${names[2]} and ${names.length - 3} more`
      : names.join(", ");
  return (
    <Card
      title="Dispositions"
      actions={
        <Link
          to="/admin/calling/dispositions"
          search={{ tab: "campaigns", campaign: id }}
          className="text-ui text-accent-ink"
        >
          Edit
        </Link>
      }
    >
      <div className="flex flex-col gap-1 px-4 py-3">
        <span className="text-caption text-ink-3">{q.isLoading ? "Loading" : line}</span>
        {q.data ? <Badge>{q.data.own ? "Own set" : "Inherited"}</Badge> : null}
      </div>
    </Card>
  );
}

/** The campaign's default voicemail drop message (ADR 0022): team messages only, inherited by
 * sub-campaigns. Hidden while the company has drops off. */
function VoicemailDefaultCard({ id }: { id: string }) {
  const q = useQuery(campaignVmDefaultQuery(id));
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (messageId: string | null) => vmDrops.setCampaignDefault(id, messageId),
    onSuccess: (v) => {
      qc.setQueryData(vmKeys.campaign(id), v);
      toast("Default voicemail saved.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const d = q.data;
  if (!d?.drops_enabled) return null;
  const options = [
    { value: "", label: "None" },
    ...d.options.map((m) => ({ value: m.id, label: `${m.name} (${clockMs(m.duration_ms)})` })),
  ];
  return (
    <Card title="Default voicemail">
      <div className="flex flex-col gap-2 px-4 py-3">
        {d.can_edit ? (
          <Select
            aria-label="Default voicemail"
            value={d.message?.id ?? ""}
            disabled={save.isPending}
            options={options}
            onValueChange={(v) => save.mutate(v || null)}
          />
        ) : (
          <span className="text-body">{d.message?.name ?? "None"}</span>
        )}
        <span className="text-caption text-ink-3">
          {!d.message && d.inherited
            ? `Uses ${d.inherited.campaign_name}'s default: ${d.inherited.message.name}`
            : "Campaigns under it use it unless they pick their own."}
        </span>
      </div>
    </Card>
  );
}

/** The company's mailbox for a brand or campaign (ADR 0037): reps' email for it goes out from
 * that mailbox unless they pick their own. Admins set it and lock it; a Rep Manager sets it on
 * campaigns they lead and sees the lock read-only. A lock above wins and shows here. */
function SendFromCard({ d }: { d: CampaignDetail }) {
  const c = d.campaign;
  const me = useMe();
  const admin = isAdmin(me);
  const root = !c.parent_id;
  const qc = useQueryClient();
  const q = useQuery(companyPicksQuery);
  const save = useMutation({
    mutationFn: mailboxesApi.putCompanyPick,
    onSuccess: (v) => {
      qc.setQueryData(mailboxKeys.companyPicks, v);
      toast("Send from saved.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  if (q.isError) return null;
  const data = q.data;
  const { own, inherited } = data
    ? pickFor(
        data.items,
        c.id,
        d.path.map((p) => p.id),
      )
    : { own: null, inherited: null };
  const lockedAbove = inherited?.locked ? inherited : null;
  // Admins anywhere; a Rep Manager on a sub-campaign of a campaign they lead, never a locked one.
  const canEdit = !lockedAbove && (admin || (!root && d.can_manage_members && !own?.locked));
  const options = [
    { value: "", label: "None" },
    ...(data?.company_mailboxes ?? []).map((o) => ({ value: o.id, label: o.address })),
  ];
  if (own && !options.some((o) => o.value === own.company_mailbox_id)) {
    options.push({ value: own.company_mailbox_id, label: own.address });
  }
  const put = (mailbox: string | null, locked: boolean) =>
    save.mutate({
      scope: root ? "brand" : "campaign",
      campaign_id: c.id,
      company_mailbox_id: mailbox,
      locked: mailbox ? locked : false,
    });
  return (
    <Card
      title={
        <span className="flex items-center gap-1">
          Send from
          <InfoButton kind="help" label="About Send from">
            Reps' email for this {nounOf(c)} goes out from this mailbox unless they pick their own. Locked,
            they can't.
          </InfoButton>
        </span>
      }
    >
      <div className="flex flex-col gap-2 px-4 py-3">
        {!data ? (
          <span className="text-caption text-ink-3">Loading</span>
        ) : lockedAbove ? (
          <span className="flex items-center gap-1.5 text-body">
            <Lock aria-hidden className="size-3 text-ink-3" />
            {lockedAbove.address}
          </span>
        ) : canEdit ? (
          <Select
            aria-label="Send from"
            value={own?.company_mailbox_id ?? ""}
            disabled={save.isPending}
            options={options}
            onValueChange={(v) => put(v || null, own?.locked ?? false)}
          />
        ) : (
          <span className="text-body">{own?.address ?? "None"}</span>
        )}
        {data && !lockedAbove ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-ui">Lock</span>
            <Switch
              label="Lock"
              checked={own?.locked ?? false}
              disabled={!admin || !own || save.isPending}
              onChange={(v) => own && put(own.company_mailbox_id, v)}
            />
          </div>
        ) : null}
        {lockedAbove ? (
          <span className="text-caption text-ink-3">Set by your admin for {lockedAbove.campaign_name}.</span>
        ) : !own && inherited ? (
          <span className="text-caption text-ink-3">
            Same as {inherited.campaign_name}: {inherited.address}
          </span>
        ) : null}
      </div>
    </Card>
  );
}
